import { FileText, Upload, Loader2, Plus, Eye, Download, Trash2 } from 'lucide-react';
import { Section } from '../ui';

// ─────────────────────────────────────────
// DOCUMENTS SECTION
// File list with per-file action buttons (view, download, analyze, delete).
// Empty state shows a big drop zone for the first upload.
// All file operations are handled by the parent — this is pure UI.
// ─────────────────────────────────────────
export default function DocumentsSection({
  files,
  uploadingFile,
  analyzingFileId,
  onUploadFile,
  onViewFile,
  onDownloadFile,
  onAnalyzeFile,
  onDeleteFile,
  setPendingFile,
}) {
  return (
    <Section
      title="Documents"
      icon={FileText}
      action={
        <label className={`flex items-center gap-1.5 px-3 py-1 rounded-lg border text-xs font-medium transition cursor-pointer ${uploadingFile ? 'border-slate-200 text-slate-400' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}>
          {uploadingFile ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />} Upload
          <input type="file" className="hidden" onChange={onUploadFile} disabled={uploadingFile} />
        </label>
      }
    >
      <div className="pt-3">
        {files.length === 0 ? (
          <label className="flex flex-col items-center justify-center gap-2 w-full border-2 border-dashed border-slate-200 rounded-xl py-8 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition">
            <Upload size={20} className="text-slate-400" />
            <span className="text-sm text-slate-500">Click to upload documents</span>
            <input type="file" className="hidden" onChange={onUploadFile} disabled={uploadingFile} />
          </label>
        ) : (
          <div className="space-y-2">
            {files.map((f) => (
              <div key={f.id} className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-200 group">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                    <FileText size={13} className="text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{f.name || f.filename}</p>
                    <p className="text-xs text-slate-400">{f.size ? `${(f.size / 1024).toFixed(1)} KB` : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0 ml-2">
                  <button
                    onClick={() => onViewFile(f.id)}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-slate-600 hover:bg-blue-50 hover:text-blue-600 transition"
                  >
                    <Eye size={13} /> <span className="hidden sm:inline">View</span>
                  </button>
                  <button
                    onClick={() => onDownloadFile(f.id, f.name || f.filename)}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-slate-600 hover:bg-blue-50 hover:text-blue-600 transition"
                  >
                    <Download size={13} /> <span className="hidden sm:inline">Download</span>
                  </button>
                  <button
                    onClick={() => { setPendingFile(null); onAnalyzeFile(f.id); }}
                    disabled={analyzingFileId === f.id}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-purple-600 hover:bg-purple-50 transition disabled:opacity-50"
                  >
                    {analyzingFileId === f.id ? <Loader2 size={13} className="animate-spin" /> : <span>✦</span>}
                    <span className="hidden sm:inline">Analyze</span>
                  </button>
                  <button
                    onClick={() => onDeleteFile(f.id)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}